import type { ReactNode } from "react"
import Link from "@/components/link"
import { ArrowRight, ArrowUpRight } from "lucide-react"

import { cn } from "@anysola/ui/utils"

/**
 * One square of the home page's mosaic: a caption row (name, a few muted
 * words, where it opens) above a piece of the real interface. The piece sits
 * on the page tone, rounded at the top only and cut off by the tile's bottom
 * edge, so it reads as a window seen through the tile rather than a card in
 * a card. Tiles are named sections; the sample interface inside is marked
 * by the caller.
 */
function Tile({
  id,
  name,
  line,
  href,
  className,
  frameClassName,
  children,
}: {
  id: string
  name: string
  line: string
  href: string
  className?: string
  frameClassName?: string
  children: ReactNode
}) {
  const external = /^https?:\/\//.test(href)
  const Arrow = external ? ArrowUpRight : ArrowRight
  return (
    <section
      aria-labelledby={id}
      className={cn(
        "flex min-w-0 flex-col overflow-hidden rounded-surface bg-card",
        className
      )}
    >
      <div className="flex items-baseline justify-between gap-4 px-5 pt-4 pb-4 sm:px-6">
        <p className="min-w-0 truncate text-sm">
          <span id={id} className="font-medium text-foreground">
            {name}
          </span>
          <span className="ml-2 text-muted-foreground">{line}</span>
        </p>
        <Link
          href={href}
          className="inline-flex min-h-6 shrink-0 items-center gap-1 rounded-sm text-sm text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          Open
          <span className="sr-only"> {name}</span>
          <Arrow aria-hidden="true" className="size-3.5" />
        </Link>
      </div>
      <div
        className={cn(
          "mx-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-t-lg bg-background sm:mx-4",
          frameClassName
        )}
      >
        {children}
      </div>
    </section>
  )
}

export { Tile }
